"use client";

import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, CARD, LABEL } from "@/components/tool-kit/styles";
import type { POST_TEST_PROBABILITY_CALCULATOR_STRINGS } from "@/components/tools/post-test-probability-calculator";
import { t } from "@/lib/i18n/interpolate";

/*
 * One test in the chain: described either by sensitivity and specificity plus its result,
 * or directly by the likelihood ratio to apply. Strings are the widget's own.
 */

type Strings = typeof POST_TEST_PROBABILITY_CALCULATOR_STRINGS;

export type TestMode = "ss" | "lr";
export type TestResult = "positive" | "negative";

export interface TestInput {
  id: number;
  mode: TestMode;
  sensitivity: string;
  specificity: string;
  lr: string;
  result: TestResult;
}

export function TestEditor({
  test,
  index,
  canRemove,
  onChange,
  onRemove,
  strings,
  formatNumber,
}: {
  test: TestInput;
  index: number;
  canRemove: boolean;
  onChange: (patch: Partial<TestInput>) => void;
  onRemove: () => void;
  strings: Strings;
  formatNumber: (value: number) => string;
}) {
  const prefix = `ptp-${test.id}`;
  return (
    <fieldset className={`${CARD} space-y-4`}>
      <legend className={`${LABEL} px-2`}>
        {t(strings.testHeading, { number: formatNumber(index + 1) })}
      </legend>
      <PillGroup
        label={strings.describeLabel}
        name={`${prefix}-mode`}
        onChange={(mode) => onChange({ mode })}
        options={[
          { value: "ss", label: strings.modeSensSpec },
          { value: "lr", label: strings.modeLr },
        ]}
        value={test.mode}
      />
      {test.mode === "ss" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id={`${prefix}-sens`}
            label={strings.sensitivity}
            numeric
            onChange={(sensitivity) => onChange({ sensitivity })}
            placeholder={strings.sensitivityPlaceholder}
            value={test.sensitivity}
          />
          <TextField
            id={`${prefix}-spec`}
            label={strings.specificity}
            numeric
            onChange={(specificity) => onChange({ specificity })}
            placeholder={strings.specificityPlaceholder}
            value={test.specificity}
          />
          <SelectField
            id={`${prefix}-result`}
            label={strings.resultLabel}
            onChange={(result) => onChange({ result })}
            options={[
              { value: "positive", label: strings.resultPositive },
              { value: "negative", label: strings.resultNegative },
            ]}
            value={test.result}
          />
        </div>
      ) : (
        <TextField
          hint={strings.lrHint}
          id={`${prefix}-lr`}
          label={strings.lrLabel}
          numeric
          onChange={(lr) => onChange({ lr })}
          placeholder={strings.lrPlaceholder}
          value={test.lr}
        />
      )}
      {canRemove ? (
        <button className={BUTTON_SMALL} onClick={onRemove} type="button">
          {strings.removeTest}
        </button>
      ) : null}
    </fieldset>
  );
}
